import { BrowserCookieStickyBucketService, GrowthBook, useGrowthBook } from '@growthbook/growthbook-react';
import Cookies from 'js-cookie';
import { getTenancy } from '../hooks/useTenancy';
import { getCountryCodeFromCookie } from '../contexts/LocaleContext';
import { getOrCreateAnonymousId } from './anonymousId';
import { requiresMarketingConsent } from './consent';

const GROWTHBOOK_FEATURE_LOAD_TIMEOUT_MS = 3_000;
// This prefix is defined in `web` repo as that is where the cookie is set
const OVERRIDE_COOKIE_PREFIX = 'fi_gb_ee_';

// These types should align with what's in the Growthbook SDK config at https://app.growthbook.io/attributes
type UserAttributes = {
  loggedIn?: boolean;
  userEncodedUuid?: string;
  anonymousId?: string;
  existingMultiDogUser?: boolean;
  purchasingOnlyMonthly?: boolean;
  tenancy?: string;
  internalTester?: boolean;
  countryCode?: string;
};

/**
 * A singleton wrapper around GrowthBook
 */
class FiGrowthBook {
  public readonly growthBook: GrowthBook;
  private loadedFeatures = false;
  // tracks the first variant seen per experiment per page load to prevent
  // multi-exposure events when evaluations race sticky bucket initialization
  // or when multiple components evaluate the same experiment flag
  private trackedExperiments = new Map<string, string>();
  // Serialized copy of the last attributes we handed to GrowthBook, used to skip
  // redundant setAttributes() calls (see setAttributes for why).
  private lastAttributesJson?: string;
  // Caller-supplied attributes accumulated across all setAttributes call sites. A call
  // site only ever adds or updates keys; omitting a key keeps its last known value.
  private callerAttributes: UserAttributes = {};

  constructor() {
    // read viewer country directly to avoid triggering the growthbook ↔ LocaleContext circular
    // dependency at module init time. getCountryCodeFromCookie() (LocaleContext) is safe to call
    // in setAttributes() after all modules have loaded, but not here in the constructor.
    const countryCode = (Cookies.get('fi_country') ?? Cookies.get('viewer_country'))?.toUpperCase();
    // disable cookie-based sticky buckets for pre-consent GDPR users — gbexp_* cookies aren't
    // strictly necessary and require consent in opt-in jurisdictions. deterministic hashing of
    // the sessionStorage ID provides equivalent within-session consistency.
    const stickyBucketService = requiresMarketingConsent(countryCode)
      ? undefined
      : new BrowserCookieStickyBucketService({ jsCookie: Cookies });

    this.growthBook = new GrowthBook({
      stickyBucketService,
      apiHost: 'https://cdn.growthbook.io',
      // This is the client key for the Fi GrowthBook project
      // It can be found at https://app.growthbook.io/sdks
      clientKey: process.env.REACT_APP_GROWTHBOOK_CLIENT_KEY,
      // Update the instance in realtime as features change in GrowthBook
      subscribeToChanges: true,
      enableDevMode: process.env.NODE_ENV !== 'production',
      // Only required for A/B testing
      // Called every time a user is put into an experiment
      trackingCallback: (experiment, result) => {
        if (this.willBeForced(experiment.key)) {
          return;
        }
        const variantValue = result.inExperiment ? result.variationId.toString() : '-1';

        // only track the first variant per experiment per page load — prevents
        // multi-exposure when sticky buckets haven't settled yet or when multiple
        // components evaluate the same flag during a single render cycle
        if (this.trackedExperiments.has(experiment.key)) {
          return;
        }
        this.trackedExperiments.set(experiment.key, variantValue);

        analytics.track('User Viewed Experiment', {
          name: experiment.key,
          variant: variantValue,
        });
      },
    });
  }

  /**
   * Load all features from GrowthBook service
   * This is only necessary on initial load as `subscribeToChanges: true` will keep the instance up to date
   * If this fails, move on gracefully which will result in default feature value being used and reload will be
   * attempted on next instance retrieval
   */
  public async loadFeatures() {
    if (!this.loadedFeatures) {
      try {
        await this.growthBook.loadFeatures({ timeout: GROWTHBOOK_FEATURE_LOAD_TIMEOUT_MS });
        this.loadedFeatures = true;
      } catch (err) {
        // If GrowthBook is unavailable, we will still be able to load the app, but still attempt to load features
        // on the next request
        console.error({ err }, 'Error loading features from GrowthBook');
      }
    }

    this.setForcedOverrides();
  }

  /**
   * Set attributes that will be used to determine which variant the user is in
   */
  public setAttributes(userAttributes?: UserAttributes): Promise<void> {
    const tenancy = getTenancy();
    const countryCode = getCountryCodeFromCookie();

    const anonymousId = getOrCreateAnonymousId();

    // analytics may be blocked (Instagram in-app browser, ad blockers) — degrade gracefully.
    const analyticsAvailable = window.analytics && typeof window.analytics.user === 'function';
    const userId = analyticsAvailable ? window.analytics.user().id() : undefined;

    // merge into the accumulated caller attributes rather than rebuilding from this call's
    // payload alone: two call sites passing different shapes (one with a key, one without)
    // would otherwise alternate the serialized form below and defeat the no-op guard, forcing
    // a GrowthBookProvider re-render on every flag evaluation - an infinite render loop when
    // the evaluating components re-render with that subtree. undefined values are dropped:
    // JSON.stringify omits them, so they can never serialize stably, and an undefined
    // attribute means "not known at this call site", not "unset".
    Object.assign(
      this.callerAttributes,
      Object.fromEntries(Object.entries(userAttributes ?? {}).filter(([, value]) => value !== undefined)),
    );

    const attributes = {
      ...this.callerAttributes,
      anonymousId,
      userEncodedUuid: userId,
      loggedIn: !!userId,
      tenancy,
      countryCode,
      url: window.location.href,
    };

    // GrowthBook's setAttributes() unconditionally calls _render(), which forces a
    // synchronous re-render of the entire GrowthBookProvider subtree. A component that
    // reads a flag *with attributes* during render (getFeatureValue triggers setAttributes)
    // and also subscribes to the GrowthBook context therefore re-triggers itself on that
    // re-render — an infinite loop. Skipping no-op writes breaks that cycle at the root and
    // avoids resetting experiment-exposure dedup. Key order is stable because
    // callerAttributes is a persistent object: existing keys keep their insertion position.
    const attributesJson = JSON.stringify(attributes);
    if (attributesJson === this.lastAttributesJson) {
      return Promise.resolve();
    }
    this.lastAttributesJson = attributesJson;

    return this.growthBook.setAttributes(attributes);
  }

  /**
   * Retrieves any force override cookies for all features and sets them in GrowthBook
   * @private
   */
  private setForcedOverrides(): void {
    const features = this.growthBook.getFeatures();
    const forcedFeatures: Map<string, any> = new Map();
    for (const [featureName] of Object.entries(features)) {
      // Check if override cookie is set
      const cookie = Cookies.get(FiGrowthBook.getCookieNameForForcedFeatureVariant(featureName));
      if (cookie) {
        // Add override to forced features
        forcedFeatures.set(featureName, coerceCookieValue(cookie));
      }
    }
    if (forcedFeatures.size > 0) {
      // Set overrides
      this.growthBook.setForcedFeatures(forcedFeatures);
    }
  }

  /**
   * Returns the value of a forced override cookie if it exists
   */
  private getForcedOverrideCookieValue(featureName: string): string | number | boolean | undefined {
    const cookie = Cookies.get(FiGrowthBook.getCookieNameForForcedFeatureVariant(featureName));
    if (cookie) {
      return coerceCookieValue(cookie);
    }
    return undefined;
  }

  /** false until the initial feature fetch succeeds; a blocked or slow cdn leaves it false for the session */
  public get featuresLoaded(): boolean {
    return this.loadedFeatures;
  }

  /**
   * Returns the value of a feature, returning a forced override cookie first if present
   */
  public getFeatureValue<T>(featureName: string, defaultValue: T, userAttributes?: UserAttributes): T {
    const cookie = this.getForcedOverrideCookieValue(featureName);
    if (cookie !== undefined) {
      return cookie as T;
    }

    userAttributes && this.setAttributes(userAttributes);
    return this.growthBook.getFeatureValue<T>(featureName, defaultValue) as T;
  }

  /**
   * Returns true if the feature is being forced
   */
  private willBeForced(featureName: string): boolean {
    return !!Cookies.get(FiGrowthBook.getCookieNameForForcedFeatureVariant(featureName));
  }

  private static getCookieNameForForcedFeatureVariant(featureName: string) {
    return `${OVERRIDE_COOKIE_PREFIX}${featureName}`;
  }
}

const fiGrowthBook = new FiGrowthBook();

export function getFiGrowthBook() {
  return fiGrowthBook;
}

export function useIsGrowthBookEnabled() {
  try {
    const growthBook = useGrowthBook();
    return !!growthBook;
  } catch {
    return false;
  }
}
/**
 * Coerce cookie string value into a boolean, number, or string
 */
function coerceCookieValue(value: string): string | number | boolean {
  if (value === 'true') {
    return true;
  } else if (value === 'false') {
    return false;
  } else if (!isNaN(Number(value))) {
    return Number(value);
  }
  return value;
}

/* Custom Types for multi-value feature flags */
export enum PromotionBannerVariant {
  DEFAULT = 'DEFAULT',
  CONTROL = 'CONTROL',
  EXPERIMENT = 'EXPERIMENT',
}
